/**
 * Provider tree. Order matters and is fixed:
 *
 *   StrictMode
 *     ClerkProvider              — owns the session
 *       ConvexProviderWithClerk  — needs Clerk's useAuth to mint Convex tokens
 *         BrowserRouter
 *           ThemeProvider
 *             App
 *
 * Convex must sit inside Clerk: it asks Clerk for a JWT on every reconnect, so
 * a Convex provider mounted above Clerk would authenticate as anonymous.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";

import App from "./App";
import { initPwa } from "./pwa";
import { ThemeProvider } from "./components/theme-provider";
import { ConfigError } from "./components/config-error";
import "./index.css";

const CONVEX_URL = import.meta.env.VITE_CONVEX_URL;
const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * Clerk ships its own palette, which does not follow our theme — its default
 * card is light, so on the dark theme the body text and the footer came out
 * unreadable. Binding its variables to the same CSS custom properties Tailwind
 * uses makes every Clerk component track whichever theme is active, including
 * a later switch to light.
 */
const clerkAppearance = {
  variables: {
    // colorPrimary and colorDanger are scale seeds — Clerk derives shades from
    // them and cannot parse a var(), so these two are literals. Both are the
    // same in either theme.
    colorPrimary: "hsl(142 76% 36%)",
    colorDanger: "hsl(0 84% 60%)",
    colorBackground: "hsl(var(--card))",
    colorText: "hsl(var(--card-foreground))",
    colorTextSecondary: "hsl(var(--muted-foreground))",
    colorTextOnPrimaryBackground: "hsl(var(--primary-foreground))",
    colorInputBackground: "hsl(var(--input))",
    colorInputText: "hsl(var(--foreground))",
    borderRadius: "var(--radius)",
  },
  elements: {
    // The social buttons take their text from Clerk's neutral scale, which is
    // seeded from a literal colour and so cannot follow the theme. Override
    // with our own tokens instead.
    socialButtonsBlockButton: "border-border",
    socialButtonsBlockButtonText: "text-card-foreground",
  },
};

const root = ReactDOM.createRoot(document.getElementById("root")!);

/**
 * Missing env vars are the single most common first-deploy failure, and the
 * raw error ("Missing publishable key") gives no hint about where to set it.
 * Render a real page instead of throwing into a blank screen.
 */
const missing: string[] = [];
if (!CONVEX_URL) missing.push("VITE_CONVEX_URL");
if (!CLERK_KEY) missing.push("VITE_CLERK_PUBLISHABLE_KEY");

if (missing.length > 0) {
  root.render(
    <React.StrictMode>
      <ConfigError missing={missing} />
    </React.StrictMode>,
  );
} else {
  const convex = new ConvexReactClient(CONVEX_URL);

  root.render(
    <React.StrictMode>
      <ClerkProvider
        publishableKey={CLERK_KEY}
        afterSignOutUrl="/"
        appearance={clerkAppearance}
      >
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <BrowserRouter>
            <ThemeProvider defaultTheme="dark" storageKey="dashbase-theme">
              <App />
            </ThemeProvider>
          </BrowserRouter>
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </React.StrictMode>,
  );
}

// Registered for both branches and after the first render. The service worker
// caches the page shell, which has nothing to do with whether the app's own
// config is present — and a misconfigured deploy is exactly when you still
// want the shell to load. Called last so a slow or failed registration can
// never delay paint.
initPwa();
